import {isMirageSupportedLanding} from './resort-cabinets.mjs';
/** Conservative static-ground safety, shared by session restore and portal exits.
 * This bounded flood-fill is not a navmesh or a general platform-jump solver. */
import {MOVEMENT,flightAllowed,worldColliders,playableBounds,withinPlayableGround} from './movement.mjs';
const cache=new WeakMap(); // Geometry-derived cache only; no player or simulation state.
const prepared=new Map(),preparing=new Map();
const MAX_PREPARED_MAPS=24;
function mapGeometry(world) {
  const bounds={...playableBounds(world)},boundsKey=[world.id==='valoria',bounds.minX,bounds.maxX,bounds.minZ,bounds.maxZ].join(':');
  const saved=cache.get(world);
  if(saved?.revision===world.revision&&saved.entities===world.entities&&saved.boxes===world.colliders&&saved.boundsKey===boundsKey)return saved;
  const colliders=worldColliders(world),key=boundsKey+':'+JSON.stringify(colliders.map(c=>[c.minX,c.maxX,c.minZ,c.maxZ,c.minY??0,c.maxY??100]));
  const result={key,bounds,boundsKey,colliders,revision:world.revision,entities:world.entities,boxes:world.colliders};
  cache.set(world,result);return result;
}
function retainMap(key,map) {
  prepared.delete(key);prepared.set(key,map);
  while(prepared.size>MAX_PREPARED_MAPS)prepared.delete(prepared.keys().next().value);
  return map;
}
function getPrepared(geometry) {
  const map=prepared.get(geometry.key);
  return map?retainMap(geometry.key,map):null;
}
const EPS=.001;
const overlaps=(p,c,r=MOVEMENT.radius)=>p.x+r>c.minX&&p.x-r<c.maxX&&p.z+r>c.minZ&&p.z-r<c.maxZ;
function clearBody(p,colliders,margin=0) {
  return colliders.every(c=>!overlaps(p,c,MOVEMENT.radius+margin)||p.y+MOVEMENT.height<=(c.minY??0)+EPS||p.y>=(c.maxY??100)-EPS);
}
function groundSegmentClear(a,b,colliders) {
  for(const c of colliders) {
    if((c.maxY??100)<=EPS||(c.minY??0)>=MOVEMENT.height-EPS)continue;
    let lo=0,hi=1;
    for(const axis of ['x','z']) {
      const min=c[axis==='x'?'minX':'minZ']-MOVEMENT.radius,max=c[axis==='x'?'maxX':'maxZ']+MOVEMENT.radius,d=b[axis]-a[axis];
      if(Math.abs(d)<1e-10) {if(a[axis]<=min||a[axis]>=max){lo=2;break;}}
      else {const one=(min-a[axis])/d,two=(max-a[axis])/d;lo=Math.max(lo,Math.min(one,two));hi=Math.min(hi,Math.max(one,two));}
    }
    if(lo<hi&&hi>0&&lo<1)return false;
  }
  return true;
}
function* buildGroundMap(world,geometry) {
  const {bounds,colliders,key}=geometry,r=MOVEMENT.radius;
  const groundWorld={id:world.id,bounds};
  const minX=bounds.minX+r,maxX=bounds.maxX-r,minZ=bounds.minZ+r,maxZ=bounds.maxZ-r;
  const cell=Math.max(1.25,Math.sqrt(Math.max(0,(maxX-minX)*(maxZ-minZ))/24000));
  const nx=Math.max(2,Math.ceil((maxX-minX)/cell)+1),nz=Math.max(2,Math.ceil((maxZ-minZ)/cell)+1),dx=(maxX-minX)/(nx-1),dz=(maxZ-minZ)/(nz-1);
  const point=index=>({x:minX+(index%nx)*dx,y:0,z:minZ+Math.floor(index/nx)*dz});
  const labels=new Int32Array(nx*nz).fill(-1),queue=new Int32Array(nx*nz);
  for(let i=0;i<labels.length;i++) {const p=point(i);if(!withinPlayableGround(p,groundWorld)||!clearBody(p,colliders))labels[i]=-2;if(i%64===63)yield;}
  let component=0,largest=-1,largestCount=0;
  for(let start=0;start<labels.length;start++) {
    if(labels[start]!==-1)continue;
    let head=0,tail=1;queue[0]=start;labels[start]=component;
    while(head<tail) {
      if(head%64===63)yield;
      const index=queue[head++],x=index%nx,z=Math.floor(index/nx),a=point(index);
      for(const [ox,oz]of [[1,0],[-1,0],[0,1],[0,-1]]) {
        const xx=x+ox,zz=z+oz;if(xx<0||xx>=nx||zz<0||zz>=nz)continue;
        const next=zz*nx+xx;
        if(labels[next]!==-1||!groundSegmentClear(a,point(next),colliders))continue;
        labels[next]=component;queue[tail++]=next;
      }
    }
    if(tail>largestCount){largestCount=tail;largest=component;}component++;
  }
  return {key,colliders,labels,largest,point,nx,nz,minX,minZ,dx,dz};
}
function groundMap(world,preparedOnly=false) {
  const geometry=mapGeometry(world),saved=getPrepared(geometry);
  if(saved||preparedOnly)return saved;
  const build=buildGroundMap(world,geometry);let result;
  do {result=build.next();}while(!result.done);
  return retainMap(geometry.key,result.value);
}
/** Browser loading/background work. Geometry-equivalent revisions reuse a map.
 * The generator yields every 64 grid operations; no flood-fill is needed in a
 * prepared-only traversal frame. A pending destination remains safely blocked. */
export function prepareSpawnSafety(world,{yieldTask=()=>new Promise(resolve=>setTimeout(resolve,0)),budgetMs=2}={}) {
  const geometry=mapGeometry(world),saved=getPrepared(geometry);
  if(saved)return Promise.resolve();
  if(preparing.has(geometry.key))return preparing.get(geometry.key);
  const request=(async()=>{
    const build=buildGroundMap(world,geometry);let result;
    do {
      await yieldTask();const start=performance.now();
      do {result=build.next();}while(!result.done&&performance.now()-start<Math.max(.1,budgetMs));
    }while(!result.done);
    retainMap(geometry.key,result.value);
  })().finally(()=>preparing.delete(geometry.key));
  preparing.set(geometry.key,request);return request;
}
export function isSpawnSafetyPrepared(world) {return !!getPrepared(mapGeometry(world));}

function connectedGround(position,world,map) {
  const p={x:position.x,y:0,z:position.z};
  if(!withinPlayableGround(p,world)||!clearBody(p,map.colliders))return false;
  const x=Math.round((p.x-map.minX)/map.dx),z=Math.round((p.z-map.minZ)/map.dz);
  for(let oz=-1;oz<=1;oz++)for(let ox=-1;ox<=1;ox++) {
    const xx=x+ox,zz=z+oz;if(xx<0||xx>=map.nx||zz<0||zz>=map.nz)continue;
    const index=zz*map.nx+xx;
    if(map.labels[index]===map.largest&&groundSegmentClear(p,map.point(index),map.colliders))return true;
  }
  return false;
}
function supportBelow(p,colliders) {
  let support=null,height=0;
  for(const c of colliders)if(overlaps(p,c)&&(c.maxY??100)>height&&(c.maxY??100)<=p.y+.025){support=c;height=c.maxY??100;}
  return {support,height};
}
function reachableSupport(p,world,map) {
  const {support,height}=supportBelow(p,map.colliders);
  if(!support)return {reachable:connectedGround(p,world,map),height};
  const peak=MOVEMENT.jumpSpeed**2/(2*Math.max(1,Math.min(40,world.gravity??18)));
  if(height>peak+.25)return {reachable:false,height};
  const margin=MOVEMENT.radius+.1,candidates=[
    {x:(support.minX+support.maxX)/2,z:support.minZ-margin},{x:(support.minX+support.maxX)/2,z:support.maxZ+margin},
    {x:support.minX-margin,z:(support.minZ+support.maxZ)/2},{x:support.maxX+margin,z:(support.minZ+support.maxZ)/2},
  ];
  return {reachable:candidates.some(point=>connectedGround(point,world,map)),height};
}
export function isSafePortalExit(position,world,{preparedOnly=false,flying=false}={}) {
  if(!position||![position.x,position.y,position.z].every(Number.isFinite)||position.y<-.001||!withinPlayableGround(position,world))return false;
  const map=groundMap(world,preparedOnly);if(!map||!clearBody(position,map.colliders))return false;
  if(flying&&flightAllowed(world)&&position.y<=MOVEMENT.flightMaxHeight)return true;
  // A player-placed reciprocal doorway itself provides access to a roof or
  // platform. Only its immediate supported landing gains this exception; an
  // unrelated inaccessible roof remains invalid for restore/legacy exits.
  const landing = (world.portals ?? []).some(portal => {
    if (!world.portalPlacements?.[portal.id]?.ownerId || !portal.targetPortalId || !portal.targetWorldId) return false;
    const dx=position.x-portal.position.x,dz=position.z-portal.position.z,c=Math.cos(portal.yaw??0),s=Math.sin(portal.yaw??0);
    if (Math.abs(dx*c-dz*s) > (portal.width??3.2)/2 + .35 || Math.abs(dx*s+dz*c) > 2 || position.y < portal.position.y-.025 || position.y > portal.position.y+(portal.height??3.4)) return false;
    const y=portal.position.y,r=MOVEMENT.radius;
    return y===0 || map.colliders.some(box => Math.abs((box.maxY??100)-y)<=.025 && position.x-r>=box.minX && position.x+r<=box.maxX && position.z-r>=box.minZ && position.z+r<=box.maxZ);
  });
  if (landing || isMirageSupportedLanding(world,position,map.colliders,MOVEMENT.radius)) return true;
  const support=reachableSupport(position,world,map),peak=MOVEMENT.jumpSpeed**2/(2*Math.max(1,Math.min(40,world.gravity??18)));
  return support.reachable&&position.y<=support.height+Math.max(8,peak+1);
}
export function findSafeSpawn(world) {
  const map=groundMap(world),requested=world.spawn??{x:0,y:0,z:0};
  if(isSafePortalExit(requested,world)&&clearBody(requested,map.colliders,.15)) {
    const support=supportBelow(requested,map.colliders);
    if(Math.abs(requested.y-support.height)<.03)return {...requested,y:support.height};
  }
  let best=null,bestDistance=Infinity;
  for(let i=0;i<map.labels.length;i++)if(map.labels[i]===map.largest) {
    const point=map.point(i);if(!clearBody(point,map.colliders,.15))continue;
    const distance=(point.x-(Number.isFinite(requested.x)?requested.x:0))**2+(point.z-(Number.isFinite(requested.z)?requested.z:0))**2;
    if(distance<bestDistance){best=point;bestDistance=distance;}
  }
  return best;
}
export function sanitizePlayerPosition(player,world) {
  const permitted=flightAllowed(world);
  // Removing permission ends hover but allows a valid high position to fall
  // naturally, rather than teleporting the player to spawn on a metadata edit.
  if(player.flying&&!permitted&&player.position&&withinPlayableGround(player.position,world)&&player.position.y>=0&&player.position.y<=MOVEMENT.flightMaxHeight&&clearBody(player.position,worldColliders(world)))return {player:{...player,flying:false,jumpBufferRemaining:0,coyoteRemaining:0},repaired:false};
  if(isSafePortalExit(player.position,world,{flying:player.flying===true}))return {player,repaired:false};
  const spawn=findSafeSpawn(world);if(!spawn)return {player,repaired:false,blocked:true};
  return {player:{...player,worldId:world.id,position:spawn,velocityX:0,velocityY:0,velocityZ:0,grounded:true,flying:false,jumpHeld:false,coyoteRemaining:0,jumpBufferRemaining:0,lastPortalId:null,portalCooldownRemaining:0},repaired:true};
}
